import React, { useState, useEffect } from 'react';

import { Order } from '@src/core/buyerApiClient';
import { useAppDispatch, useAppSelector, useWidgetsTheme } from '@src/core/hooks';
import { buyerSelector } from '@src/features/auth';
import Checkout from '@src/components/Checkout/Checkout';
import AuthDisplay from '@src/components/Auth/AuthDisplay';
import { currentOrderRescheduleSelector } from '@src/features/sale';
import { setCurrentOrder, fetchOrderRequiredFormInputs } from '@src/features/order';
import Spinner, { SpinnerWrapper } from '@src/components/reusable/spinner/Spinner';

import MyAccount from './MyAccount';
import SaleDisplay from './SaleDisplay';

const LOADING_MODE = 'loading_mode';
const AUTH_MODE = 'auth_mode';
const START_MODE = 'start_mode';
const SALE_MODE = 'sale_mode';
const CHECKOUT_MODE = 'checkout_mode';

const Account = () => {
  const dispatch = useAppDispatch();
  const [mode, setMode] = useState(LOADING_MODE);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);
  const [, logged] = useAppSelector(buyerSelector);
  const currentOrderReschedule = useAppSelector(currentOrderRescheduleSelector(selectedOrderId));
  const actionColor = useWidgetsTheme('actionColor');

  useEffect(() => {
    if (logged) {
      setMode(START_MODE);
    } else if (logged === false) {
      setMode(AUTH_MODE);
    }
  }, [logged]);

  const onOrderSelection = (order: Order) => {
    setSelectedOrderId(order.id);
    setMode(SALE_MODE);
  };

  const onOrderCheckout = async (order: Order) => {
    try {
      dispatch(setCurrentOrder(order, [])); // Define current order, so checkout component can work properly

      setMode(LOADING_MODE);

      await dispatch(fetchOrderRequiredFormInputs(order));
    } finally {
      setMode(CHECKOUT_MODE);
    }
  };

  const handleCloseSale = () => {
    setMode(START_MODE);
    setSelectedOrderId(null);
  };

  return (
    <div>
      {(() => {
        switch (mode) {
          case LOADING_MODE:
            return (
              <SpinnerWrapper>
                <Spinner visible spinnerColor={actionColor} />
              </SpinnerWrapper>
            );
          case AUTH_MODE:
            return <AuthDisplay onLogin={() => setMode(START_MODE)} />;
          case START_MODE:
            return <MyAccount onOrderSelection={onOrderSelection} onOrderCheckout={onOrderCheckout} />;
          case SALE_MODE:
            return (
              <SaleDisplay
                onOrderCheckout={onOrderCheckout}
                order={currentOrderReschedule!}
                onClose={handleCloseSale}
              />
            );
          case CHECKOUT_MODE:
            return <Checkout editable={false} skipResumeCart={true} skipConfirmation={true} />;
          default:
            return null;
        }
      })()}
    </div>
  );
};

export default Account;
